/**
 * Centralized Task option lists — backend-driven (single source of truth).
 *
 * Master data (categories, content_types, statuses, developer/uiux/approval
 * statuses, dependencies, priorities, tags, output_types) lives in the
 * `task_options` MongoDB collection, served by GET /api/task-options/options.
 *
 * Screens consume via the `useTaskOptions()` hook (mirrors
 * `useSocialMediaOptions`). A module-level cache is shared across all hook
 * instances so we only hit the API once per session; `refreshTaskOptions(token)`
 * force-invalidates the cache after any Task Options admin CRUD write.
 *
 * FALLBACKs are used only until the first API response arrives. They mirror
 * the historic hardcoded lists so first-paint of any Task screen never
 * renders empty dropdowns.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Fallbacks (until the API response arrives) ──────────────────────────────
const CATEGORIES_FALLBACK = [
  'Technical SEO', 'On-Page', 'Off-Page', 'UI/UX Design',
  'SEO', 'QA/Technical', 'Google Ads', 'Meta Ads', 'Others',
];
const CONTENT_TYPES_FALLBACK = [
  'Website Blog', 'LinkedIn Article', 'Landing Page',
  'Web Page Content', 'Social Media Post', 'Other',
];
const TASK_STATUSES_FALLBACK = ['Backlog', 'Yet to start', 'In Process', 'On Hold', 'Completed'];
const DEV_STATUSES_FALLBACK = ['Yet to Start', 'In Development', 'Assets Pending', 'Need to Live', 'Live Done', 'Completed'];
const UIUX_STATUSES_FALLBACK = ['Yet to Start', 'In Research', 'Wireframing', 'Designing', 'In Review', 'Revisions', 'Completed'];
const APPROVAL_STATUSES_FALLBACK = ['Pending', 'Approved', 'Rejected', 'Standby', 'Not Required'];
const DEPENDENCY_TYPES_FALLBACK = ['All', 'Developer', 'UI/UX', 'Both', 'None'];
const PRIORITIES_FALLBACK = ['Low', 'Medium', 'High', 'Critical'];
const OUTPUT_TYPES_FALLBACK = [];
const TAGS_FALLBACK = [];

const FALLBACK = {
  CATEGORIES: CATEGORIES_FALLBACK,
  CONTENT_TYPES: CONTENT_TYPES_FALLBACK,
  TASK_STATUSES: TASK_STATUSES_FALLBACK,
  DEVELOPER_STATUSES: DEV_STATUSES_FALLBACK,
  UIUX_STATUSES: UIUX_STATUSES_FALLBACK,
  APPROVAL_STATUSES: APPROVAL_STATUSES_FALLBACK,
  DEPENDENCY_TYPES: DEPENDENCY_TYPES_FALLBACK,
  PRIORITIES: PRIORITIES_FALLBACK,
  OUTPUT_TYPES: OUTPUT_TYPES_FALLBACK,
  TAGS: TAGS_FALLBACK,
  // Full option items (with color/canonical_key/id) — needed for badges + reports
  RAW: {},
};

// ─── Module-level cache shared across all hook instances ─────────────────────
let cachedOptions = null;
let inFlight = null;
const listeners = new Set();

const normalize = (apiPayload = {}) => {
  const raw = apiPayload || {};
  const namesOf = (list) => (Array.isArray(list) ? list.map((x) => x.name).filter(Boolean) : []);
  return {
    CATEGORIES: namesOf(raw.categories).length ? namesOf(raw.categories) : CATEGORIES_FALLBACK,
    CONTENT_TYPES: namesOf(raw.content_types).length ? namesOf(raw.content_types) : CONTENT_TYPES_FALLBACK,
    TASK_STATUSES: namesOf(raw.task_statuses).length ? namesOf(raw.task_statuses) : TASK_STATUSES_FALLBACK,
    DEVELOPER_STATUSES: namesOf(raw.developer_statuses).length ? namesOf(raw.developer_statuses) : DEV_STATUSES_FALLBACK,
    UIUX_STATUSES: namesOf(raw.uiux_statuses).length ? namesOf(raw.uiux_statuses) : UIUX_STATUSES_FALLBACK,
    APPROVAL_STATUSES: namesOf(raw.manager_approval_statuses).length ? namesOf(raw.manager_approval_statuses) : APPROVAL_STATUSES_FALLBACK,
    DEPENDENCY_TYPES: namesOf(raw.dependency_types).length ? namesOf(raw.dependency_types) : DEPENDENCY_TYPES_FALLBACK,
    PRIORITIES: namesOf(raw.priorities).length ? namesOf(raw.priorities) : PRIORITIES_FALLBACK,
    OUTPUT_TYPES: namesOf(raw.output_types),
    TAGS: namesOf(raw.tags),
    RAW: raw,
  };
};

const fetchOptions = (token) => {
  if (!token) return Promise.resolve(FALLBACK);
  if (!inFlight) {
    inFlight = axios
      .get(`${API}/task-options/options`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const norm = normalize(res.data || {});
        cachedOptions = norm;
        listeners.forEach((cb) => cb(norm));
        return norm;
      })
      .catch(() => FALLBACK)
      .finally(() => { inFlight = null; });
  }
  return inFlight;
};

/**
 * Force-refresh the cached task options. Call this after any admin mutation
 * on `/task-options/admin/options/*` so all consumers pick up the change
 * immediately (no full page reload required).
 */
export const refreshTaskOptions = async (token) => {
  cachedOptions = null;
  return fetchOptions(token);
};

/**
 * React hook — returns the latest cached options. Automatically fetches on
 * first mount and subscribes to future cache updates.
 */
export const useTaskOptions = () => {
  const { token } = useAuth();
  const [options, setOptions] = useState(cachedOptions || FALLBACK);

  useEffect(() => {
    let mounted = true;
    // Subscribe to future cache updates (e.g. after refreshTaskOptions())
    const listener = (next) => { if (mounted) setOptions(next); };
    listeners.add(listener);
    // Initial fetch
    if (!cachedOptions) {
      fetchOptions(token).then((v) => { if (mounted) setOptions(v); });
    }
    return () => { mounted = false; listeners.delete(listener); };
    // `cachedOptions`/`fetchOptions`/`listeners` are module-level singletons
    // (shared cache across every hook instance), not reactive component
    // state — intentionally excluded so this effect only re-runs on `token`.
  }, [token]);

  return options;
};

/**
 * Given a name and a list_type, return the color hex configured for it in
 * Task Options (or null if not set). Consumers should combine this with
 * their existing fallback CSS-class map so unset colors still render nicely.
 */
export const getOptionColor = (rawGroup, name) => {
  if (!Array.isArray(rawGroup) || !name) return null;
  const hit = rawGroup.find((x) => (x?.name || '').toLowerCase() === String(name).toLowerCase());
  return hit?.color || null;
};

/**
 * Convenience: return an inline-style {backgroundColor, color, borderColor}
 * derived from an option's stored color. Renders a soft tinted badge
 * consistent with the rest of the design system.
 */
export const optionBadgeStyle = (hex) => {
  if (!hex) return null;
  return {
    backgroundColor: `${hex}18`,          // ~10% opacity fill
    color: hex,
    borderColor: `${hex}55`,              // ~33% opacity border
  };
};

/**
 * Given a raw option group (e.g. RAW.uiux_statuses) and a display NAME
 * currently stored on a task, return its immutable canonical_key. This is
 * what lets workflow logic (tab bucketing, "is this Completed?" checks,
 * approve/reject action mapping) survive an admin rename — the task's
 * stored name always mirrors the CURRENT active name after a rename
 * cascade, so looking it up here always resolves to the right key.
 */
export const canonicalKeyForName = (rawGroup, name) => {
  if (!Array.isArray(rawGroup) || !name) return null;
  const hit = rawGroup.find((x) => (x?.name || '').toLowerCase() === String(name).toLowerCase());
  return hit?.canonical_key || null;
};

/**
 * Inverse of `canonicalKeyForName` — given a canonical_key, return the
 * CURRENT active display name for it (or `fallback` if archived/missing).
 * Used to resolve hardcoded workflow actions (e.g. "approve" -> whatever
 * Task Options currently calls the "approved" status) to the live name.
 */
export const nameForCanonicalKey = (rawGroup, key, fallback = null) => {
  if (!Array.isArray(rawGroup) || !key) return fallback;
  const hit = rawGroup.find((x) => x?.canonical_key === key);
  return hit ? hit.name : fallback;
};

// Legacy default-export shim: previous callers imported CONTENT_TYPES as a
// static array from '../utils/contentTypes'. We now re-export the fallbacks
// under the same names so downstream imports don't break during the migration.
// Consumers should migrate to useTaskOptions() to receive live values.
export const CONTENT_TYPES = CONTENT_TYPES_FALLBACK;
export const TASK_CATEGORIES = CATEGORIES_FALLBACK;
